
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TreeDeciduous, Droplet, Fish, Earth, Plus, CalendarDays, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

// Datos para mostrar diferentes impactos por día
const impactData = [
  {
    id: 'water',
    title: 'Ahorro de agua',
    description: 'Eligiendo comer vegano y saludable, hoy has ahorrado +1.500 litros comparada con una dieta omnívora.',
    equivalences: [
      '🚿 13 duchas',
      '🧼 30 lavadoras llenas',
      '☕ 375 tazas de café',
      '🚽 125 descargas de cisterna (12 l por descarga)'
    ],
    icon: <Droplet className="h-6 w-6 text-blue-500" />,
    color: 'from-blue-50 to-cyan-50',
    borderColor: 'border-blue-200',
    iconBg: 'bg-blue-100',
    dailyValue: 1500,
    unit: 'litros'
  },
  {
    id: 'animals',
    title: 'Vidas salvadas',
    description: 'Hoy has salvado a +1,6 vidas. Según promedios anuales, cada persona vegana salva unos 575 animales al año.',
    equivalences: [
      '🐔 1 pollo o 2 peces al día',
      '🐷 Cada 3 días, estás evitando que un cerdo o una vaca muera',
      '🐟 En 30 días, podrías evitar la muerte de más de 40 peces'
    ],
    icon: <Fish className="h-6 w-6 text-emerald-500" />,
    color: 'from-emerald-50 to-green-50',
    borderColor: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    dailyValue: 1.6,
    unit: 'vidas'
  },
  {
    id: 'emissions',
    title: 'Reducción de emisiones',
    description: 'Hoy, gracias a elegir una alimentación vegana has reducido las emisiones de gases de efecto invernadero en ~3,6 kg de CO₂.',
    equivalences: [
      '🚗 Conducir 28 km en un coche de gasolina',
      '🚌 Un trayecto de 4 horas en autobús',
      '💻 Ver 170 horas de Netflix',
      '✈️ 15 minutos en avión comercial',
      '🌳 Lo que absorben 0,2 árboles en 1 año'
    ],
    extraInfo: '👉 En un mes comiendo vegano, ahorras más CO₂ que un viaje ida y vuelta Madrid–Valencia en coche (840km). O un vuelo ida y vuelta Madrid–París (unos 1.100 km de vuelo en total). 🔋 O argar tu móvil 13.500 veces',
    icon: <Earth className="h-6 w-6 text-indigo-500" />,
    color: 'from-indigo-50 to-purple-50',
    borderColor: 'border-indigo-200',
    iconBg: 'bg-indigo-100',
    dailyValue: 3.6,
    unit: 'kg CO₂'
  },
  {
    id: 'land',
    title: 'Uso de tierra',
    description: 'Eligiendo hoy comer vegano has ahorrado +4 metros cuadrados de tierra agrícola usada para pastos, monocultivos de pienso y producción ganadera.',
    equivalences: [
      '🌻 El espacio de 2 mesas grandes de comedor',
      '🌽 Tierra suficiente para cultivar legumbres para 3 comidas',
      '🏡 Lo que ocuparía 1 jardín pequeño o balcón urbano'
    ],
    extraInfo: '👉 A largo plazo, esto evita la deforestación, erosión y pérdida de hábitat.',
    icon: <TreeDeciduous className="h-6 w-6 text-green-500" />,
    color: 'from-green-50 to-lime-50',
    borderColor: 'border-green-200',
    iconBg: 'bg-green-100',
    dailyValue: 4,
    unit: 'm²'
  }
];

const ImpactBanner: React.FC = () => {
  const [currentImpact, setCurrentImpact] = useState(impactData[0]);
  const [showEquivalences, setShowEquivalences] = useState(false);
  const [cumulativeSavings, setCumulativeSavings] = useState({
    daysCount: 0,
    water: 0,
    animals: 0,
    emissions: 0,
    land: 0
  });
  
  const { user } = useAuth();
  
  // Función para resetear el contador de impacto
  const resetImpactCounter = () => {
    const resetValues = {
      daysCount: 0,
      water: 0,
      animals: 0,
      emissions: 0,
      land: 0
    };
    
    // Guardar los valores reseteados en localStorage
    localStorage.setItem('cumulative-impact', JSON.stringify(resetValues));
    localStorage.removeItem('impact-banner-date'); // Eliminar la fecha para que cuente hoy nuevamente
    
    // Actualizar el estado
    setCumulativeSavings(resetValues);
    
    toast.success('¡Tu contador de impacto positivo ha sido reiniciado!', {
      description: 'Ahora puedes comenzar a acumular tu impacto desde el día 1.'
    });
  };
  
  useEffect(() => {
    // Seleccionar un impacto basado en el día del año
    const today = new Date();
    const dayOfYear = Math.floor((today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) / 86400000);
    const impactIndex = dayOfYear % impactData.length;
    setCurrentImpact(impactData[impactIndex]);
    
    // También podemos guardar la fecha actual para cambiar solo cuando cambie el día
    const currentDate = today.toDateString();
    const lastShownDate = localStorage.getItem('impact-banner-date');
    
    // Manejo de acumulación de impacto
    const storedCumulative = localStorage.getItem('cumulative-impact');
    let cumulative = storedCumulative ? JSON.parse(storedCumulative) : {
      daysCount: 0,
      water: 0,
      animals: 0,
      emissions: 0,
      land: 0
    };
    
    if (lastShownDate !== currentDate) {
      // Nuevo día, sumamos el impacto
      cumulative.daysCount += 1;
      cumulative.water += impactData[0].dailyValue;
      cumulative.animals += impactData[1].dailyValue;
      cumulative.emissions += impactData[2].dailyValue;
      cumulative.land += impactData[3].dailyValue;
      
      localStorage.setItem('impact-banner-date', currentDate);
      localStorage.setItem('cumulative-impact', JSON.stringify(cumulative));
    }
    
    setCumulativeSavings(cumulative);

    // Auto mostrar las equivalencias después de un breve retraso
    const timer = setTimeout(() => {
      setShowEquivalences(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);
  
  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toFixed(num % 1 === 0 ? 0 : 1);
  };
  
  return (
    <div className="space-y-4">
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="rounded-xl shadow-md border overflow-hidden bg-gradient-to-br from-vegana-primary/20 to-vegana-secondary/20 border-vegana-primary/30"
      >
        <div className="p-4 relative">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-vegana-primary" />
              <h3 className="font-semibold text-sm text-gray-700">
                Impacto acumulado en {cumulativeSavings.daysCount} días
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-xs text-gray-500 italic">
                Actualizado diariamente
              </div>
              {user && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={resetImpactCounter}
                  className="flex items-center gap-1"
                  title="Resetear contador"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  <span className="sr-only sm:not-sr-only sm:inline-block">Resetear</span>
                </Button>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
            <div className="bg-blue-50 rounded-lg p-2 border border-blue-100 flex items-center gap-2">
              <Droplet className="h-4 w-4 text-blue-500" />
              <div>
                <p className="text-xs text-gray-600">Agua</p>
                <p className="font-semibold text-blue-700">{formatNumber(cumulativeSavings.water)} litros</p>
              </div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-100 flex items-center gap-2">
              <Fish className="h-4 w-4 text-emerald-500" />
              <div>
                <p className="text-xs text-gray-600">Vidas</p>
                <p className="font-semibold text-emerald-700">{formatNumber(cumulativeSavings.animals)} animales</p>
              </div>
            </div>
            <div className="bg-indigo-50 rounded-lg p-2 border border-indigo-100 flex items-center gap-2">
              <Earth className="h-4 w-4 text-indigo-500" />
              <div>
                <p className="text-xs text-gray-600">CO₂</p>
                <p className="font-semibold text-indigo-700">{formatNumber(cumulativeSavings.emissions)} kg</p>
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-2 border border-green-100 flex items-center gap-2">
              <TreeDeciduous className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-xs text-gray-600">Tierra</p>
                <p className="font-semibold text-green-700">{formatNumber(cumulativeSavings.land)} m²</p>
              </div>
            </div>
          </div>
          
          <div className="text-xs text-center mt-2 text-gray-500">
            <Plus className="h-3 w-3 inline-block mr-1" />
            Cada día vegano que eliges suma a este impacto positivo
          </div>
        </div>
      </motion.div>
    
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className={`rounded-xl shadow-md border overflow-hidden bg-gradient-to-br ${currentImpact.color} ${currentImpact.borderColor}`}
      >
        <div className="p-4 sm:p-6 relative">
          <div className="flex flex-col items-center mb-4">
            <div className={`${currentImpact.iconBg} p-3 rounded-lg mb-2`}>
              {currentImpact.icon}
            </div>
            <h3 className="font-semibold text-lg text-gray-800 text-center">
              {currentImpact.title}
            </h3>
          </div>
          
          <div className="flex-1">
            <p className="text-gray-700 font-medium text-center mb-4">
              {currentImpact.description}
            </p>
          </div>
        
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ 
              opacity: showEquivalences ? 1 : 0,
              height: showEquivalences ? 'auto' : 0
            }}
            transition={{ duration: 0.5 }}
            className="mt-3 bg-white/50 backdrop-blur-sm rounded-lg p-3 border border-gray-100"
          >
            <p className="font-medium text-gray-700 mb-2">¿A qué equivale?</p>
            <ul className="space-y-1.5">
              {currentImpact.equivalences.map((eq, index) => (
                <motion.li 
                  key={index}
                  initial={{ opacity: 0, x: -5 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * (index + 1) }}
                  className="text-sm text-gray-600"
                >
                  {eq}
                </motion.li>
              ))}
            </ul>
            
            {currentImpact.extraInfo && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 + (currentImpact.equivalences.length * 0.1) }}
                className="mt-2 text-sm font-medium text-gray-700 italic"
              >
                {currentImpact.extraInfo}
              </motion.p>
            )}
          </motion.div>
          
          <div className="mt-3 text-sm text-gray-500 italic text-center">
            Cada día que eliges una alimentación vegana, haces una diferencia positiva en el planeta.
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ImpactBanner;
